<script setup lang="ts">
import { useI18n } from "vue-i18n"
import type { Client, CreatePrefill } from "@/entities/client"
import { useClientSearch, MIN_QUERY_LENGTH } from "@/entities/client"
import { Avatar, Input } from "@/shared/ui"

defineOptions({ name: "ClientPicker" })

defineProps<{
  modelValue: Client | null
}>()

const emit = defineEmits<{
  "update:modelValue": [Client]
  requestCreate: [CreatePrefill]
}>()

const { t } = useI18n()
const { query, results, limited, loading, setQuery } = useClientSearch()

function parseQuery(q: string): CreatePrefill {
  const trimmed = q.trim()
  const phoneMatch = trimmed.match(/[+\d][\d\s\-()]{6,}/)
  if (!phoneMatch) return { firstName: trimmed }
  const phone = phoneMatch[0].replace(/[\s\-()]/g, "")
  const firstName = trimmed.replace(phoneMatch[0], "").trim() || undefined
  return { firstName, phone }
}

function handleAddNew(): void {
  emit("requestCreate", parseQuery(query.value))
}
</script>

<template>
  <div class="client-picker" data-testid="client-picker">
    <Input
      :model-value="query"
      type="search"
      :placeholder="t('records.createFlow.clientStep.searchPlaceholder')"
      data-testid="client-picker-search"
      autocomplete="off"
      @update:model-value="setQuery($event as string)"
    />

    <p
      v-if="query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH"
      class="client-picker__hint"
    >
      {{ t("records.createFlow.clientStep.minLengthHint") }}
    </p>

    <p v-else-if="loading" class="client-picker__hint">
      {{ t("records.createFlow.clientStep.searching") }}
    </p>

    <template v-else-if="results.length > 0">
      <ul class="client-picker__list" role="listbox">
        <li v-for="client in results" :key="client.id" role="option">
          <button
            type="button"
            class="client-picker__row"
            :data-testid="`client-picker-row-${client.id}`"
            @click="emit('update:modelValue', client)"
          >
            <Avatar :name="`${client.firstName} ${client.lastName}`.trim()" size="sm" />
            <span class="client-picker__row-info">
              <span class="client-picker__name">{{ [client.firstName, client.lastName].filter(Boolean).join(" ") }}</span>
              <span class="client-picker__phone">{{ client.phone }}</span>
            </span>
          </button>
        </li>
      </ul>
      <p v-if="limited" class="client-picker__limited">
        {{ t("records.createFlow.clientStep.limited") }}
      </p>
    </template>

    <div
      v-else-if="query.trim().length >= MIN_QUERY_LENGTH && !loading"
      class="client-picker__empty"
      data-testid="client-picker-empty"
    >
      <p class="client-picker__empty-title">{{ t("records.createFlow.clientStep.emptyTitle") }}</p>
      <button
        type="button"
        class="client-picker__add-new"
        data-testid="btn-add-new-client"
        @click="handleAddNew"
      >
        {{ t("records.createFlow.clientStep.addNew") }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.client-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.client-picker__hint {
  font-size: 0.8125rem;
  color: var(--muted-foreground);
  padding: 4px 0;
}

.client-picker__list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.client-picker__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  border-bottom: 1px solid var(--border);
}

.client-picker__row:last-child {
  border-bottom: none;
}

.client-picker__row:hover {
  background: var(--accent);
}

.client-picker__row:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: -2px;
}

.client-picker__row-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.client-picker__name {
  font-size: 0.875rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.client-picker__phone {
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

.client-picker__limited {
  font-size: 0.75rem;
  color: var(--muted-foreground);
  padding: 4px 0;
}

.client-picker__empty {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
}

.client-picker__empty-title {
  font-size: 0.875rem;
  color: var(--muted-foreground);
}

.client-picker__add-new {
  font-size: 0.875rem;
  color: var(--primary);
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
}

.client-picker__add-new:focus-visible {
  outline: 2px solid var(--ring);
  border-radius: 2px;
}
</style>
